import React, {useEffect, useMemo, useState} from "react";
import {Box, Chip, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select} from "@mui/material";
import {get,keyBy} from "lodash";
import {Clear} from "@mui/icons-material";
import Flex from "../layouts/Flex";

const FormikSelectField = ({ formik, name, label, options, size, multiple, helperText: helperTextProp, autoClose = true }) => {
  const formikValue = get(formik.values, name);
  const [value, setValue] = useState(formikValue ?? (multiple ? [] : ''));
  const [open, setOpen] = useState(false);
  const hasError = formik.touched[name] && Boolean(formik.errors[name]);

  useEffect(() => {
    setValue(formikValue ?? (multiple ? [] : ''));
  }, [formikValue, multiple]);

  const helperText = useMemo(() => {
    if (hasError) {
      return formik.errors[name];
    }
    return helperTextProp || '';
  }, [formik, name, hasError, helperTextProp]);

  const hasEmptyOption = !multiple && options.some(option => !option.value);
  const optionsByValue = useMemo(() => {
    return keyBy(options, 'value');
  }, [options]);

  const shrink = useMemo(() => {
    if (multiple) {
      return value.length > 0;
    } else {
      return value || hasEmptyOption;
    }
  }, [multiple, value, hasEmptyOption]);

  const handleUpdate = (newValue) => {
    setValue(newValue);
    formik.setFieldValue(name, newValue);
  }

  const handleRemoveItem = (item) => {
    handleUpdate(value.filter((v) => v !== item));
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  const renderer = multiple ? ((selected) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((v) => (
        <Chip
          key={v}
          label={optionsByValue[v]?.label || v}
          size="small"
          sx={{ fontSize: "0.75rem", maxHeight: "20px", zIndex: 100000 }}
          onMouseDown={(e) => e.stopPropagation()}
          onDelete={() => handleRemoveItem(v)}
        />
      ))}
    </Box>
  )) : null;

  return (
    <FormControl fullWidth size={size} error={hasError}>
      <InputLabel id={`${name}-label`} shrink={Boolean(shrink)}>{label}</InputLabel>
      <Box sx={{ position: "relative" }}>
        <Select
          labelId={`${name}-label`}
          id={name}
          value={value}
          label={label}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(event) => {
            setValue(event.target.value);
            formik.setFieldValue(name, event.target.value);
            if (autoClose) {
              setOpen(false);
            }
          }}
          renderValue={renderer}
          displayEmpty={hasEmptyOption}
          variant="outlined"
          multiple={multiple}
          fullWidth
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {((multiple && value.length > 0) || (!multiple && value)) && (
          <Flex justifyContent="center" alignItems="center" sx={{ position: "absolute", right: 32, top: "50%", transform: "translateY(-50%)" }}>
            <IconButton
              size="small"
              onClick={() => {
                const emptyValue = multiple ? [] : '';
                setValue(emptyValue);
                formik.setFieldValue(name, emptyValue);
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </Flex>
        )}
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default FormikSelectField;